import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { LocalDate, NumberDate, NumberMonth } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import {
  KirjanpitajanTiimi,
  KirjanpitajanTiimikokonaisuus,
  KirjanpitajanTiimikokonaisuusVarastossa,
  KirjanpitajanTiiminJasen,
  KirjanpitajanTiimiTiimikokonaisuudessa,
  KirjanpitajanTiimiVarastossa,
  KirjanpitajaTiimiAvaimet,
  KirjanpitajienTiimit,
} from '../model/kirjanpitaja'

interface MuunnettuMuoto {
  tiimit: Partial<Record<KirjanpitajanTiimi['avain'], KirjanpitajanTiimi>>
  tiimikokonaisuudet: Partial<Record<KirjanpitajanTiimikokonaisuus['avain'], KirjanpitajanTiimikokonaisuus>>
}

/**
 * Kutsutaan kerran konstruktorista. Implementaatio (front: snapshot-kuuntelija, funktiopuoli: alkulataus + 2 h ajastin)
 * kytkee tietolähteen ja kutsuu provideData-callbackia aina kun uusi dokumentti on saatavilla.
 */
export type KirjanpitajienTiimitDataSource = (provideData: (dokumentti: KirjanpitajienTiimit) => void) => void

export class KirjanpitajanTiimiService {
  isSpeccer(avain: string): boolean {
    return (
      avain === 'dqUiLNmLRaMNbyNgMhWG1AUmVGI2' || // Valtteri
      avain === '4sNRp7LvWTeZ9WTomoKOA9jD42y1' || // Ville
      avain === 'AHiu6xZimwXgxmd3d5vqzbC8ce42' || // Jon
      avain === 'xN2SGgB8KGWKkN634npJGmzknPs2' || // Sari
      avain === '0oUrQvhhY5eFxnLA8obhKZx5Xi63' || // Mikko
      avain === 'H6KU6K9S39QaqDnpe1apaUV3dsz1' // Elina
    )
  }

  // Päivitetään suoraan jokaisella provideData-kutsulla, jotta seuraavat luvut näkevät tuoreen arvon välittömästi
  private _muunnettu: MuunnettuMuoto | null = null
  // Resolvataan ensimmäisellä provideData-kutsulla; ennen sitä tehdyt anna*-kutsut odottavat tätä
  private _odottavaResolve: ((muoto: MuunnettuMuoto) => void) | null = null
  private _odottavaPromise: Promise<MuunnettuMuoto> = new Promise(resolve => {
    this._odottavaResolve = resolve
  })

  constructor(
    private _dateService: DateService,
    private _copyService: KopioijaPalvelu,
    dataSource: KirjanpitajienTiimitDataSource,
  ) {
    // Annetaan kutsujalle callback, jolla dataa työnnetään palveluun. Tämä erottaa tietolähteen valinnan
    // (snapshot-kuuntelija, ajastettu lataus, testivakio) palvelun toteutuksesta.
    dataSource(dokumentti => this._provideData(dokumentti))
  }

  private _provideData(dokumentti: KirjanpitajienTiimit): void {
    if (!dokumentti || !dokumentti.tiimit || !dokumentti.tiimikokonaisuudet) {
      throw new Error('Annettu kirjanpitajien-tiimit -dokumentti puuttuu tai on virheellinen')
    }
    const tiimit: MuunnettuMuoto['tiimit'] = {}
    for (const [avain, varasto] of Object.entries(dokumentti.tiimit) as [KirjanpitajanTiimi['avain'], KirjanpitajanTiimiVarastossa | undefined][]) {
      if (varasto) {
        tiimit[avain] = this._muunnaTiimi(varasto)
      }
    }
    const tiimikokonaisuudet: MuunnettuMuoto['tiimikokonaisuudet'] = {}
    for (const [avain, varasto] of Object.entries(dokumentti.tiimikokonaisuudet) as [KirjanpitajanTiimikokonaisuus['avain'], KirjanpitajanTiimikokonaisuusVarastossa | undefined][]) {
      if (varasto) {
        tiimikokonaisuudet[avain] = this._muunnaTiimikokonaisuus(varasto)
      }
    }
    const muunnettu: MuunnettuMuoto = { tiimit, tiimikokonaisuudet }
    this._muunnettu = muunnettu
    if (this._odottavaResolve) {
      this._odottavaResolve(muunnettu)
      this._odottavaResolve = null
    }
  }

  private _annaMuunnettu(): Promise<MuunnettuMuoto> {
    if (this._muunnettu) {
      return Promise.resolve(this._muunnettu)
    }
    return this._odottavaPromise
  }

  private _muunnaTiimi(varasto: KirjanpitajanTiimiVarastossa): KirjanpitajanTiimi {
    return {
      avain: varasto.avain,
      nimi: varasto.nimi,
      alku: varasto.alku,
      loppu: varasto.loppu,
      esihenkilot: Object.values(varasto.esihenkilot).flat(),
      jasenet: Object.values(varasto.jasenet).flat(),
    }
  }

  private _muunnaTiimikokonaisuus(varasto: KirjanpitajanTiimikokonaisuusVarastossa): KirjanpitajanTiimikokonaisuus {
    return {
      avain: varasto.avain,
      nimi: varasto.nimi,
      alku: varasto.alku,
      loppu: varasto.loppu,
      esihenkilot: Object.values(varasto.esihenkilot).flat(),
      tiimit: varasto.tiimit,
    }
  }

  private async _annaTiimit(): Promise<KirjanpitajanTiimi[]> {
    const muoto = await this._annaMuunnettu()
    return Object.values(muoto.tiimit).filter((t): t is KirjanpitajanTiimi => !!t)
  }

  private async _annaKokonaisuudet(): Promise<KirjanpitajanTiimikokonaisuus[]> {
    const muoto = await this._annaMuunnettu()
    return Object.values(muoto.tiimikokonaisuudet).filter((k): k is KirjanpitajanTiimikokonaisuus => !!k)
  }

  async annaTiimitJotaKirjanpitajaVetaa(kirjanpitajaAvain: string, date: NumberDate): Promise<KirjanpitajanTiimi[]> {
    const tiimit = await this._annaTiimit()
    return tiimit.filter(tiimi => {
      for (const esihenkilo of tiimi.esihenkilot) {
        if (esihenkilo.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(esihenkilo, date)) {
          return true
        }
      }
      return false
    })
  }

  async annaKokonaisuudetJotaKirjanpitajaVetaa(kirjanpitajaAvain: string, date: NumberDate): Promise<KirjanpitajanTiimikokonaisuus[]> {
    const kokonaisuudet = await this._annaKokonaisuudet()
    return kokonaisuudet.filter(kokonaisuus => {
      for (const esihenkilo of kokonaisuus.esihenkilot) {
        if (esihenkilo.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(esihenkilo, date)) {
          return true
        }
      }
      return false
    })
  }

  async onkoKirjanpitajaKuulunutJoskusTiimiin(kirjanpitajaAvain: string, tiimi: KirjanpitajanTiimi): Promise<boolean> {
    if (!kirjanpitajaAvain || !tiimi) {
      return false
    }
    for (const kp of tiimi.esihenkilot) {
      if (kp.kirjanpitajaAvain === kirjanpitajaAvain) {
        return true
      }
    }
    for (const kp of tiimi.jasenet) {
      if (kp.kirjanpitajaAvain === kirjanpitajaAvain) {
        return true
      }
    }
    return false
  }

  async onkoKirjanpitajaJossakinTiimissa(kirjanpitajaAvain: string, ...tiimit: KirjanpitajanTiimi['avain'][]): Promise<boolean> {
    if (!kirjanpitajaAvain) {
      return false
    }
    const kirjanpitajanTiimit = await this.annaKirjanpitajanTiimit(kirjanpitajaAvain, this._dateService.currentNumberDate())
    return kirjanpitajanTiimit.some(tiimi => {
      return tiimi?.tiimi?.avain && tiimit.includes(tiimi.tiimi.avain)
    })
  }

  async annaTiimikokonaisuus(tiimikokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain']): Promise<KirjanpitajanTiimikokonaisuus> {
    const muoto = await this._annaMuunnettu()
    const kokonaisuus = muoto.tiimikokonaisuudet[tiimikokonaisuusAvain]
    if (kokonaisuus) {
      return kokonaisuus
    }
    throw new Error('Kokonaisuutta ei löytynyt avaimella ' + tiimikokonaisuusAvain)
  }

  async annaTiimi(tiimiAvain: KirjanpitajanTiimi['avain']): Promise<KirjanpitajanTiimi> {
    const muoto = await this._annaMuunnettu()
    const tiimi = muoto.tiimit[tiimiAvain]
    if (tiimi) {
      return tiimi
    }
    throw new Error('Tiimiä ei löytynyt avaimella ' + tiimiAvain)
  }

  annaTiiminKirjanpitajatPaivamaaraValilleSet(tiimi: KirjanpitajanTiimi, start: NumberDate, end: NumberDate): Set<string> {
    const set: Set<string> = new Set()
    for (const kp of tiimi.esihenkilot) {
      if (this.onkoAktiivinenPaivamaaraValilla(kp, start, end)) {
        set.add(kp.kirjanpitajaAvain)
      }
    }
    for (const kp of tiimi.jasenet) {
      if (this.onkoAktiivinenPaivamaaraValilla(kp, start, end)) {
        set.add(kp.kirjanpitajaAvain)
      }
    }
    return set
  }

  async annaTiimikokonaisuuksienKirjanpitajatPaivalleSet(tiimikokonaisuudet: Iterable<KirjanpitajanTiimikokonaisuus>, date: NumberDate): Promise<Set<string>> {
    const set: Set<string> = new Set()
    for (const tiimikokonaisuus of tiimikokonaisuudet) {
      await this.annaTiimikokonaisuudenKirjanpitajatPaivalleSet(tiimikokonaisuus, date, set)
    }
    return set
  }

  async annaTiimikokonaisuudenKirjanpitajatPaivalleSet(tiimikokonaisuus: KirjanpitajanTiimikokonaisuus, date: NumberDate, collectToSet?: Set<string>): Promise<Set<string>> {
    const set: Set<string> = collectToSet ?? new Set()
    for (const tiimiKokonaisuudessa of tiimikokonaisuus.tiimit) {
      if (this.onkoAktiivinenPaivamaarana(tiimiKokonaisuudessa, date)) {
        const tiimi = await this.annaTiimi(tiimiKokonaisuudessa.avain)
        for (const kp of tiimi.esihenkilot) {
          if (this.onkoAktiivinenPaivamaarana(kp, date)) {
            set.add(kp.kirjanpitajaAvain)
          }
        }
        for (const kp of tiimi.jasenet) {
          if (this.onkoAktiivinenPaivamaarana(kp, date)) {
            set.add(kp.kirjanpitajaAvain)
          }
        }
      }
    }
    return set
  }

  async annaTiimikokonaisuudenKirjanpitajatPaivamaaraValilleSet(tiimikokonaisuus: KirjanpitajanTiimikokonaisuus, start: NumberDate, end: NumberDate): Promise<Set<string>> {
    const set: Set<string> = new Set()
    for (const tiimiKokonaisuudessa of tiimikokonaisuus.tiimit) {
      if (this.onkoAktiivinenPaivamaaraValilla(tiimiKokonaisuudessa, start, end)) {
        const tiimi = await this.annaTiimi(tiimiKokonaisuudessa.avain)
        for (const kp of tiimi.esihenkilot) {
          if (this.onkoAktiivinenPaivamaaraValilla(kp, start, end)) {
            set.add(kp.kirjanpitajaAvain)
          }
        }
        for (const kp of tiimi.jasenet) {
          if (this.onkoAktiivinenPaivamaaraValilla(kp, start, end)) {
            set.add(kp.kirjanpitajaAvain)
          }
        }
      }
    }
    return set
  }

  annaTiiminKirjanpitajatPaivalleSet(tiimi: KirjanpitajanTiimi, date: NumberDate): Set<string> {
    const set: Set<string> = new Set()
    for (const kp of tiimi.esihenkilot) {
      if (this.onkoAktiivinenPaivamaarana(kp, date)) {
        set.add(kp.kirjanpitajaAvain)
      }
    }
    for (const kp of tiimi.jasenet) {
      if (this.onkoAktiivinenPaivamaarana(kp, date)) {
        set.add(kp.kirjanpitajaAvain)
      }
    }
    return set
  }

  annaTiiminKirjanpitajatIlmanRajauksia(tiimi: KirjanpitajanTiimi): Set<string> {
    const set: Set<string> = new Set()
    for (const kp of tiimi.esihenkilot) {
      set.add(kp.kirjanpitajaAvain)
    }
    for (const kp of tiimi.jasenet) {
      set.add(kp.kirjanpitajaAvain)
    }
    return set
  }

  kuuluukoKirjanpitajaTiimiin(tiimi: KirjanpitajanTiimi, kirjanpitajaAvain: string, date: NumberDate): boolean {
    for (const kp of tiimi.esihenkilot) {
      if (kp.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(kp, date)) {
        return true
      }
    }
    for (const kp of tiimi.jasenet) {
      if (kp.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(kp, date)) {
        return true
      }
    }
    return false
  }

  async annaKirjanpitajanTiimit(kirjanpitajaAvain: string, date: NumberDate): Promise<{ primary: boolean; tiimi: KirjanpitajanTiimi }[]> {
    const tiimit = await this.annaTiimitPaivalle(date)
    const tulokset: { primary: boolean; tiimi: KirjanpitajanTiimi }[] = []
    for (const tiimi of tiimit) {
      for (const kp of tiimi.esihenkilot) {
        if (kp.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(kp, date)) {
          tulokset.push({ primary: !!kp.primary, tiimi: tiimi })
        }
      }
      for (const kp of tiimi.jasenet) {
        if (kp.kirjanpitajaAvain === kirjanpitajaAvain && this.onkoAktiivinenPaivamaarana(kp, date)) {
          tulokset.push({ primary: !!kp.primary, tiimi: tiimi })
        }
      }
    }
    return tulokset
  }

  async annaTiimienKokonaisuudet(tiimiavaimet: KirjanpitajanTiimi['avain'][], date: NumberDate): Promise<Set<KirjanpitajanTiimikokonaisuus>> {
    const kokonaisuudet = await this.annaKaikkiTiimikokonaisuudet()
    const tulokset: Set<KirjanpitajanTiimikokonaisuus> = new Set()
    for (const tiimi of tiimiavaimet) {
      for (const kokonaisuus of kokonaisuudet) {
        for (const tiimiKokonaisuudessa of kokonaisuus.tiimit) {
          if (tiimiKokonaisuudessa.avain === tiimi && this.onkoAktiivinenPaivamaarana(tiimiKokonaisuudessa, date)) {
            tulokset.add(kokonaisuus)
          }
        }
      }
    }
    return tulokset
  }

  /**
   * Palauttaa ensimmäisen tiimin, joka on merkitty ensisijaiseksi jos tällaista ei löydy, ensimmäisen tiimin joka ei ole primääri.
   * @param kirjanpitajaAvain
   * @param date
   * @returns
   */
  async annaKirjanpitajanEnsimmainenTiimiPrimaariTiedolla(kirjanpitajaAvain: string, date: NumberDate): Promise<{ primary: boolean; tiimi: KirjanpitajanTiimi }> {
    const tiimit = await this.annaKirjanpitajanTiimit(kirjanpitajaAvain, date)
    const ensimmainenPrimaryTeam = tiimit.find(t => !!t.primary)
    if (ensimmainenPrimaryTeam) {
      return ensimmainenPrimaryTeam
    }
    if (tiimit.length) {
      return tiimit[0]
    }
    return null
  }

  /**
   * Palauttaa ensimmäisen tiimin, joka on merkitty ensisijaiseksi jos tällaista ei löydy, ensimmäisen tiimin joka ei ole primääri.
   * @param kirjanpitajaAvain
   * @param date
   * @returns
   */
  async annaKirjanpitajanEnsimmainenTiimi(kirjanpitajaAvain: string, date: NumberDate): Promise<KirjanpitajanTiimi> {
    const tiimi = await this.annaKirjanpitajanEnsimmainenTiimiPrimaariTiedolla(kirjanpitajaAvain, date)
    return tiimi?.tiimi ?? null
  }

  onkoAktiivinenKuukautena(tiimi: KirjanpitajanTiimi | KirjanpitajanTiiminJasen | KirjanpitajanTiimiTiimikokonaisuudessa, month: NumberMonth): boolean {
    const asLocal = this._dateService.numberToLocalMonth(month)
    const kuunEnsimmainenLocal: LocalDate = { year: asLocal.year, month: asLocal.month, day: 1 }
    const start = this._dateService.localDateToNumber(kuunEnsimmainenLocal)
    const end = this._dateService.localDateToNumber(this._dateService.kuukaudenViimeinenPaikallinen(kuunEnsimmainenLocal))
    return this.onkoAktiivinenPaivamaaraValilla(tiimi, start, end)
  }

  onkoAktiivinenPaivamaarana(tiimi: KirjanpitajanTiimi | KirjanpitajanTiiminJasen | KirjanpitajanTiimiTiimikokonaisuudessa, date: NumberDate): boolean {
    if (tiimi.loppu) {
      return this._dateService.compareNumberDates(date, '>=', tiimi.alku) && this._dateService.compareNumberDates(date, '<=', tiimi.loppu)
    }
    return this._dateService.compareNumberDates(date, '>=', tiimi.alku)
  }

  onkoAktiivinenPaivamaaraValilla(tiimi: KirjanpitajanTiimi | KirjanpitajanTiiminJasen | KirjanpitajanTiimiTiimikokonaisuudessa, start: NumberDate, end: NumberDate): boolean {
    if (tiimi.loppu) {
      return this._dateService.compareNumberDates(tiimi.alku, '<=', end) && this._dateService.compareNumberDates(start, '<=', tiimi.loppu)
    }
    return this._dateService.compareNumberDates(tiimi.alku, '<=', end)
  }

  async annaTiimitPaivamaaraValille(start: NumberDate, end: NumberDate): Promise<KirjanpitajanTiimi[]> {
    const tiimit = await this._annaTiimit()
    return tiimit
      .filter(tiimi => this.onkoAktiivinenPaivamaaraValilla(tiimi, start, end))
      .map(tiimi => {
        const copy: KirjanpitajanTiimi = this._copyService.cloneObjectDeep(tiimi)
        copy.esihenkilot = copy.esihenkilot.filter(henkilo => this.onkoAktiivinenPaivamaaraValilla(henkilo, start, end))
        copy.jasenet = copy.jasenet.filter(henkilo => this.onkoAktiivinenPaivamaaraValilla(henkilo, start, end))
        return copy
      })
  }

  async annaTiimitPaivalle(date: NumberDate): Promise<KirjanpitajanTiimi[]> {
    const tiimit = await this._annaTiimit()
    return tiimit
      .filter(tiimi => this.onkoAktiivinenPaivamaarana(tiimi, date))
      .map(tiimi => {
        const copy: KirjanpitajanTiimi = this._copyService.cloneObjectDeep(tiimi)
        copy.esihenkilot = copy.esihenkilot.filter(henkilo => this.onkoAktiivinenPaivamaarana(henkilo, date))
        copy.jasenet = copy.jasenet.filter(henkilo => this.onkoAktiivinenPaivamaarana(henkilo, date))
        return copy
      })
  }

  private viimeiseksiAvaimet: Set<KirjanpitajanTiimi['avain']> = new Set([
    KirjanpitajaTiimiAvaimet.IT,
    KirjanpitajaTiimiAvaimet.MUUT,
    KirjanpitajaTiimiAvaimet.KAYTTOTUKI,
    KirjanpitajaTiimiAvaimet.SALES,
    KirjanpitajaTiimiAvaimet.PALKKA,
  ])
  async annaKaikkiTiimit(): Promise<KirjanpitajanTiimi[]> {
    const tiimit = await this._annaTiimit()
    return [...tiimit].sort((a, b) => {
      if (this.viimeiseksiAvaimet.has(a.avain) && !this.viimeiseksiAvaimet.has(b.avain)) {
        return 1
      }
      if (!this.viimeiseksiAvaimet.has(a.avain) && this.viimeiseksiAvaimet.has(b.avain)) {
        return -1
      }
      return a.nimi.localeCompare(b.nimi)
    })
  }

  async annaKaikkiTiimikokonaisuudet(): Promise<KirjanpitajanTiimikokonaisuus[]> {
    const kokonaisuudet = await this._annaKokonaisuudet()
    return [...kokonaisuudet]
  }

  getHardcodedSalesTeamOptions(): { avain: string; nimi: string; aktiivinen: boolean }[] {
    return [
      { avain: 'QL4CBlJTLdQLtbb4k8AURFYeg3I3', nimi: 'Sanna Vihersaari', aktiivinen: true },
      { avain: 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2', nimi: 'Holvi Zen', aktiivinen: true },
      { avain: 'e0Ravrw1L3cBTKfYSG1D6U8sU2o1', nimi: 'Laura Pajunen', aktiivinen: false },
      { avain: 'qR99vZ0JoTfthtSpB7GLp89zjb63', nimi: 'Mikaela Gardemeister', aktiivinen: true },
      { avain: 'hZo5I0KWkrfs7eoIpAhx4foqXCC2', nimi: 'Petra Pasula', aktiivinen: true },
      { avain: 'tst8k0sa81QUu2OrOtHiBPAbPZh1', nimi: 'Hanna Räsänen', aktiivinen: true },
      { avain: 'hakalat', nimi: 'Hakalat', aktiivinen: true },
      { avain: 'lemonator_system', nimi: 'Muu', aktiivinen: true },
    ]
  }
}
